import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";

import { getIcon } from "@apps/mf-overview/src/assets/icons";
import { colors } from "@apps/mf-overview/src/ts/config";

import { InitialSlide } from "@apps/mf-overview/src/ts/components/layout/Header/transitions/InitialSlide";
import { injectStore } from "@apps/mf-overview/src/ts/stores/injectStore";
import { Store } from "@apps/mf-overview/src/ts/stores/Store";

/**
 * The header is rendered at the top of the page, containing basic ui elements
 * such as logo and profile data.
 */
// tslint:disable-next-line variable-name
const UnwrappedHeader: React.FC<{ visible?: boolean; store: Store }> = observer(
  (props: { visible?: boolean; store: Store }) => (
    <InitialSlide in={props.visible}>
      {(ref) => (
        <header ref={ref} className={`${props.store.isMobile ? "mobile" : ""}`}>
          <i className={`logo ${props.store.isMobile ? "mobile" : ""}`}>
            {getIcon("gyldendal", { fillColor: colors.black })}
          </i>

          <h1>
            {props.store.isMobile ? (
              <FormattedMessage
                id="Header__productTitle__mobile"
                defaultMessage="Forløb"
              />
            ) : (
              <FormattedMessage
                id="Header__productTitle"
                defaultMessage="Forløbsbygger"
              />
            )}
          </h1>

          <style jsx>{`
            header {
              /** Fixate header at the top of the viewport */
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              z-index: 100;

              /** Apply a background behind the header */
              background: rgba(255, 255, 255, 0.96);

              /** Apply sizing and margins to the header */
              box-sizing: border-box;
              height: 110px;
              padding: 30px;
              margin-bottom: 30px;

              /** Add border at the top of the header */
              border-top: 5px solid #3c4349;

              /** Setup flexbox */
              display: flex;
              align-items: center;

              &.mobile {
                /** Downsize header on mobile */
                padding: 23px 30px;
                height: 75px;

                /** Margin below will be setup by overview itself on mobile */
                margin-bottom: 0;

                /** Create box-shadow below header on mobile */
                box-shadow: rgba(0, 0, 0, 0.11) 0 0 50px 10px;

                /**
                         * Ensure box-shadow stays on top of content while
                         * running animations
                         */
                -webkit-transform: translate3d(0, 0, 100px);

                & h1 {
                  font-size: 24px;
                }
              }
            }

            i.logo {
              /** Set up font styling */
              display: inline-block;
              width: 30px;
              height: 45px;
              flex-shrink: 0;

              /** Apply a bit of margin between logo and title */
              margin-right: 15px;

              &.mobile {
                /** Downsize logo on mobile */
                width: 20px;
                height: 30px;
              }
            }

            h1 {
              /** Reset default margins */
              margin: 0;
              padding: 0;

              /** Apply font styling */
              font-size: 36px;
              font-weight: normal;
              line-height: 40px;
            }
          `}</style>
        </header>
      )}
    </InitialSlide>
  ),
);

// tslint:disable-next-line variable-name
export const Header = injectStore((store) => ({ store }), UnwrappedHeader);
